import { AxiosError, AxiosInstance } from "axios";
import {
  CreateCompanyAccountRequest,
  EditCompanyAccountRequest,
} from "../types/CompanyAccountRequest";
import { ValidPaymentProvider } from "../types/ValidPaymentProvider";

export const createAppApiClient = (api: AxiosInstance) => ({
  getCountryCodes: getCountryCodes(api),
  changeBotLanguage: changeBotLanguage(api),
  createCompany: createCompany(api),
  getCompany: getCompany(api),
  editCompany: editCompany(api),
  createAdminAccount: createAdminAccount(api),
  initTransaction: initTransaction(api),
  getEmployees: getEmployees(api),
  getEmployee: getEmployee(api),
  addEmployee: addEmployee(api),
  editEmployee: editEmployee(api),
  deleteEmployee: deleteEmployee(api),
  getAccessToken: getAccessToken(api),
  getCurrentUser: getCurrentUser(api),
  deleteCurrentUser: deleteCurrentUser(api),
  getAccountDeletionState: getAccountDeletionState(api),
  acceptTerms: acceptTerms(api),
  getBotAccessToken: getBotAccessToken(api),
  getBotFilters: getBotFilters(api),
  applyBotFilter: applyBotFilter(api),

  // Billing
  getPaymentStatus: getPaymentStatus(api),
  getPaymentHistory: getPaymentHistory(api),
  cancelSubscription: cancelSubscription(api),
  getPricing: getPricing(api),
  setPaymentMethod: setPaymentMethod(api),
  getInvoices: getInvoices(api),
  downloadInvoicePdf: downloadInvoicePdf(api),

  // Feedback
  feedback: feedback(api),

  // Conversations
  getConversations: getConversations(api),
  searchConversation: searchConversation(api),
  getConversationById: getConversationById(api),
  archiveConversation: archiveConversation(api),
  createConversation: createConversation(api),
  renameConversation: renameConversation(api),
  getSourceById: getSourceById(api),
  getSourceDownloadUrl: getSourceDownloadUrl(api),

  // Message Feedback
  createMessageFeedback: createMessageFeedback(api),

  // Voice Input
  transcribeAudio: transcribeAudio(api),
  // Demo Booking
  demoBooking: demoBooking(api),

  // SCAIT
  getCredits: getCredits(api),
  getCreditPricing: getCreditPricing(api),
  purchaseCredits: purchaseCredits(api),
  generateToken: generateToken(api),

  contactSupport: contactSupport(api),

  requestScaitCredit: requestScaitCredit(api),
});

export type BotLanguageRequest = {
  language: string;
};

const changeBotLanguage =
  (api: AxiosInstance) =>
  async (req: BotLanguageRequest): Promise<void> => {
    try {
      const response = await api.post("/language", req);
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      throw new Error(err.response?.data?.message);
    }
  };

export type CountryCodeResponse = {
  CountryName: string;
  CountryCode: string;
  CountryCodeId: number;
};

const getCountryCodes =
  (api: AxiosInstance) => async (): Promise<CountryCodeResponse[]> => {
    try {
      const response = await api.get("/api/country-codes/?skip=0&limit=500");
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      throw new Error(err.response?.status.toString());
    }
  };

const createCompany =
  (api: AxiosInstance) =>
  async (req: CreateCompanyAccountRequest): Promise<void> => {
    try {
      const response = await api.post("/api/company/", req);
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      throw new Error(err.response?.data?.message);
    }
  };

export type CompanyResponse = {
  CompanyName: string;
  CompanySize: number;
  Department: string;
  Website: string;
  PhysicalStreet: string;
  PhysicalCity: string;
  PhysicalStateProvince: string;
  PhysicalZipCode: string;
  PhysicalCountry: number;
  Checkbox: true;
  MailingStreet: string;
  MailingCity: string;
  MailingStateProvince: string;
  MailingZipCode: string;
  MailingCountry: number;
  CompanyId: number;
  SubscriptionPlan: number;
  CardBrand: ValidPaymentProvider | null;
  CardExpirationMonth: string | null;
  CardExpirationYear: string | null;
  CardMasked: string | null;
};

const getCompany =
  (api: AxiosInstance) => async (): Promise<CompanyResponse> => {
    try {
      const response = await api.get("/api/company/");
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      throw new Error(err.response?.data?.message);
    }
  };

const editCompany =
  (api: AxiosInstance) =>
  async (
    companyId: number,
    req: EditCompanyAccountRequest,
  ): Promise<CompanyResponse> => {
    try {
      const response = await api.put(`/api/company/${companyId}`, req);
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      throw new Error(err.response?.data?.message);
    }
  };

export type AdminAccountRequest = {
  first_name: string;
  middle_name?: string;
  last_name: string;
  suffix: string;
  work_title: string;
  mobile: string;
  work_phone?: string;
};

const createAdminAccount =
  (api: AxiosInstance) =>
  async (req: AdminAccountRequest): Promise<void> => {
    try {
      const response = await api.put("/api/company/admin-account", req);
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      throw new Error(err.response?.data?.message);
    }
  };

export type InitTransactionRequest = {
  error_url: string;
  success_url: string;
  cancel_url: string;
};

export type TransactionResponse = {
  transaction_id: string;
};

const initTransaction =
  (api: AxiosInstance) =>
  async (
    flow: "registration" | "update-payment" = "registration",
    req?: InitTransactionRequest,
  ): Promise<TransactionResponse> => {
    const baseURL = window.location.origin;
    const request = req ?? {
      error_url: `${baseURL}/payment-failure`,
      success_url: `${baseURL}/payment-success?flow=${flow}`,
      cancel_url: `${baseURL}/payment-failure`,
    };
    try {
      const response = await api.post("/api/billing/init-transaction", request);
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      throw new Error(err.response?.data?.message);
    }
  };

export type EmployeeResponse = {
  FirstName: string;
  MiddleName: string;
  LastName: string;
  WorkTitle: string;
  WorkEmail: string;
  Mobile: string;
  WorkPhone: string;
  Role: number;
  EmployeeId: number;
};

const getEmployees =
  (api: AxiosInstance) => async (): Promise<EmployeeResponse[]> => {
    try {
      const response = await api.get("/api/employees/?skip=0&limit=2000");
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      throw new Error(err.response?.data?.message);
    }
  };

const getEmployee =
  (api: AxiosInstance) =>
  async (employeeId: number): Promise<EmployeeResponse> => {
    try {
      const response = await api.get(`/api/employees/${employeeId}`);
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      throw new Error(err.response?.data?.message);
    }
  };

export type EmployeeRequest = {
  FirstName: string;
  MiddleName: string;
  LastName: string;
  WorkTitle: string;
  WorkEmail: string;
  Mobile: string;
  WorkPhone: string;
  Role: number;
  BlockingFlag?: boolean;
};

const addEmployee =
  (api: AxiosInstance) =>
  async (req: EmployeeRequest): Promise<EmployeeResponse> => {
    const response = await api.post(`/api/employees/`, req);
    return response.data;
  };

const editEmployee =
  (api: AxiosInstance) =>
  async (
    employeeId: number,
    req: EmployeeRequest,
  ): Promise<EmployeeResponse> => {
    try {
      const response = await api.put(`/api/employees/${employeeId}`, req);
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      throw new Error(err.response?.data?.message);
    }
  };

const deleteEmployee =
  (api: AxiosInstance) =>
  async (employeeId: number): Promise<EmployeeResponse> => {
    try {
      const response = await api.delete(`/api/employees/${employeeId}`);
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      throw new Error(err.response?.data?.message);
    }
  };

type AccessTokenRequest = {
  token: string; // b2c token
  policy: string; // b2c policy
};

type AccessTokenResponse = {
  jwt: string; // app access token
};

const getAccessToken =
  (api: AxiosInstance) =>
  async (req: AccessTokenRequest): Promise<AccessTokenResponse> => {
    try {
      const response = await api.post("/api/azure-b2c/token/validate", req);
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      throw new Error(err.code);
    }
  };

export type CurrentUserResponse = {
  CompanyId: number;
  FirstName: string;
  MiddleName: string;
  LastName: string;
  WorkTitle: string;
  WorkEmail: string;
  Mobile: string;
  WorkPhone: string;
  Role: number;
  EmployeeId: number;
  BlockingFlag: boolean;
  TermsAccepted: boolean;
};

const getCurrentUser =
  (api: AxiosInstance) =>
  async (): Promise<CurrentUserResponse | undefined> => {
    try {
      const response = await api.get("/api/employees/me");
      return response.data;
    } catch (error) {}
  };

const deleteCurrentUser = (api: AxiosInstance) => async (): Promise<void> => {
  await api.delete("/api/employees/me");
};

export type AccountDeletionState = {
  NeedsAdminReassignment: boolean;
  NewAdmin?: EmployeeResponse;
};

const getAccountDeletionState =
  (api: AxiosInstance) =>
  async (): Promise<AccountDeletionState> => {
    const response = await api.get("/api/employees/me/deletion-state");
    return response.data;
  };


const acceptTerms =
  (api: AxiosInstance) =>
  async (): Promise<CurrentUserResponse | undefined> => {
    try {
      const response = await api.patch("/api/employees/me/accept-terms");
      return response.data;
    } catch (error) {}
  };

const getBotAccessToken =
  (api: AxiosInstance) =>
  async (): Promise<{ access_token: string } | undefined> => {
    const response = await api.post("/chatbot/auth/header");
    return response.data;
  };

export type BotFilterType = {
  ParentBotFilterCategoryId?: number;
  BotFilterCategoryId: number;
  NameDe: string;
  NameIt: string;
  NameFr: string;
  DescriptionDe?: string;
  DescriptionIt?: string;
  DescriptionFr?: string;
};

export type BotFilterTypeExtended = BotFilterType & {
  applyPrevious?: boolean;
};

export type BotFiltersResponse = BotFilterType[];

const getBotFilters =
  (api: AxiosInstance) => async (): Promise<BotFiltersResponse> => {
    const response = await api.get("/api/ai/categories");
    return response.data;
  };

const applyBotFilter =
  (api: AxiosInstance) =>
  async (req: {
    bot_filter_category_id: number;
    conversation_id: number;
  }): Promise<{ status: string; message: string } | undefined> => {
    try {
      const response = await api.post("/api/ai/categories/select", req);
      return response.data;
    } catch (error) {
      return;
    }
  };

export type PaymentMethod = "card" | "qr_invoice";

export type PaymentStatusResponse = {
  paid: boolean;
  next_payment_amount: 0;
  has_card: boolean;
  subscription_cancelled?: boolean;
  payment_method?: PaymentMethod;
  next_payment_date?: string;
};

const getPaymentStatus =
  (api: AxiosInstance) => async (): Promise<PaymentStatusResponse> => {
    const response = await api.get("/api/billing/payment-status");
    return response.data;
  };

export enum TransactionStatus {
  PENDING = "pending",
  COMPLETED = "completed",
  FAILED = "failed",
}

export type PaymenTransactionResponse = {
  transaction_reference: string;
  amount: number;
  status: TransactionStatus;
  timestamp: string;
};

const getPaymentHistory =
  (api: AxiosInstance) => async (): Promise<PaymenTransactionResponse[]> => {
    const response = await api.get("/api/billing/payment-history");
    return response.data;
  };

const cancelSubscription =
  (api: AxiosInstance) => async (): Promise<string> => {
    const response = await api.post("/api/billing/cancel-subscription");
    return response.data;
  };

export type SetPaymentMethodRequest = {
  payment_method: PaymentMethod;
};

const setPaymentMethod =
  (api: AxiosInstance) =>
  async (req: SetPaymentMethodRequest): Promise<{ status: string }> => {
    const response = await api.post("/api/billing/payment-method", req);
    return response.data;
  };

export enum InvoiceStatus {
  Issued = "issued",
  Reminded = "reminded",
  Paid = "paid",
  Blocked = "blocked",
}

export type InvoiceResponse = {
  invoice_id: number;
  company_id: number;
  period_start: string;
  period_end: string;
  issue_date: string;
  due_date: string;
  grace_end_date: string;
  amount: number;
  status: InvoiceStatus;
  qr_reference: string;
  paid_date: string | null;
};

const getInvoices =
  (api: AxiosInstance) => async (): Promise<InvoiceResponse[]> => {
    const response = await api.get("/api/billing/invoices");
    return response.data;
  };

const downloadInvoicePdf =
  (api: AxiosInstance) =>
  async (invoiceId: number): Promise<Blob> => {
    const response = await api.get(`/api/billing/invoices/${invoiceId}/pdf`, {
      responseType: "blob",
    });
    return response.data;
  };

export type FeedbackRequest = {
  feedback: string;
};

const feedback =
  (api: AxiosInstance) =>
  async (req: FeedbackRequest): Promise<void> => {
    const response = await api.post(`/api/ai/feedback`, req);
    return response.data;
  };

export type PricingResponse = {
  student: number;
  pro: number;
  sbv_member: number;
  student_official: number;
  pro_official: number;
  sbv_member_official: number;
};

const getPricing =
  (api: AxiosInstance) => async (): Promise<PricingResponse> => {
    try {
      const response = await api.get("/api/billing/subscription-plan-prices");
      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      throw new Error(err.response?.status.toString());
    }
  };

export type ConversationResponse = {
  conversation_id: number;
  title: string;
  employee_id: number;
  created_date: string;
  last_modified_date: string;
  is_archived: boolean;
};

export type MessageSource = {
  document_source_id: string;
  document_source_type: "standard" | "sia";
  name: string;
  document_source_available: boolean;
  display_name: string;
  pages: number[];
};

export type MessageSourceDetails = {
  document_source_id: string;
  document_source_type: "standard" | "sia";
  name: string;
  content: string;
  document_source_available: boolean;
  display_name: string;
  pages: number[];
};

export type ActionMessageResponse = {
  action_id: number;
  message_id: number;
  name: string;
  value: string;
};

export type ConversationMessageResponse = {
  message_id: number;
  role: "user" | "bot";
  language: "Deutsch" | "Français" | "Italiano";
  bot_filter_category_id: number;
  timestamp: string;
  content: string;
  sources: MessageSource[] | null;
  feedback: MessageFeedbackResponse | null;
  actions: ActionMessageResponse[] | null;
  has_sia_reference: boolean;
};

export type ConversationDetailsResponse = ConversationResponse & {
  messages: ConversationMessageResponse[];
  bot_filter_category_id?: number;
};

// Conversations
const getConversations =
  (api: AxiosInstance) => async (): Promise<ConversationResponse[]> => {
    const response = await api.get("/api/conversations/");
    return response.data;
  };

export type ConversationSearchResponse = {
  conversation_id: number;
  title: string;
  employee_id: number;
  created_date: string;
  last_modified_date: string;
  is_archived: boolean;
  message: ConversationMessageResponse;
};

const searchConversation =
  (api: AxiosInstance) =>
  async (keyword?: string): Promise<ConversationSearchResponse[]> => {
    const params = keyword ? { keyword } : {};
    const response = await api.get("/api/conversations/search", { params });
    return response.data;
  };

const getConversationById =
  (api: AxiosInstance) =>
  async (conversationId: number): Promise<ConversationDetailsResponse> => {
    const response = await api.get(`/api/conversations/${conversationId}`);
    return response.data;
  };

const archiveConversation =
  (api: AxiosInstance) =>
  async (conversationId: number): Promise<ConversationResponse> => {
    const response = await api.post(
      `/api/conversations/${conversationId}/archive`,
    );
    return response.data;
  };

type ConversationRequest = {
  language: string;
  bot_filter_category_id: number;
};

const createConversation =
  (api: AxiosInstance) =>
  async (req: ConversationRequest): Promise<ConversationResponse> => {
    const response = await api.post(`/api/conversations/`, req);
    return response.data;
  };

const renameConversation =
  (api: AxiosInstance) =>
  async (
    conversationId: number,
    title: string,
  ): Promise<ConversationResponse> => {
    const response = await api.put(
      `/api/conversations/${conversationId}/title`,
      { title },
    );
    return response.data;
  };

export type GetSourceByIdRequest = {
  conversationId: number;
  messageId: number;
  sourceId: number;
};

const getSourceById =
  (api: AxiosInstance) =>
  async (req: GetSourceByIdRequest): Promise<MessageSourceDetails> => {
    const { conversationId, messageId, sourceId } = req;
    const response = await api.get(
      `/api/conversations/${conversationId}/messages/${messageId}/sources/${sourceId}`,
    );
    return response.data;
  };

export type GetSourceDownloadUrlRequest = {
  conversationId: number;
  messageId: number;
  sourceId: number;
};

export type GetSourceDownloadUrlResponse = {
  url: string;
};

const getSourceDownloadUrl =
  (api: AxiosInstance) =>
  async (
    req: GetSourceDownloadUrlRequest,
  ): Promise<GetSourceDownloadUrlResponse> => {
    const { conversationId, messageId, sourceId } = req;
    const response = await api.get(
      `/api/conversations/${conversationId}/messages/${messageId}/sources/${sourceId}/download-url`,
    );
    return response.data;
  };

// Message Feedback
export enum MessageFeedbackTypeEnum {
  POSITIVE = "positive",
  NEGATIVE = "negative",
}

export type MessageFeedbackRequest = {
  conversation_id: number;
  message_id: number;
  feedback: MessageFeedbackTypeEnum;
  reason: string;
  comment: string;
};

export type MessageFeedbackResponse = {
  feedback_id: number;
  conversation_id: number;
  message_id: number;
  feedback: MessageFeedbackTypeEnum;
  reason: string;
  comment: string;
  created_date: string;
};

const createMessageFeedback =
  (api: AxiosInstance) =>
  async (req: MessageFeedbackRequest): Promise<MessageFeedbackResponse> => {
    const response = await api.post(`/api/feedbacks/`, req);
    return response.data;
  };

export type TranscribeAudioResponse = {
  text?: string;
  transcript?: string;
};

const transcribeAudio =
  (api: AxiosInstance) =>
  async (formData: FormData): Promise<TranscribeAudioResponse> => {
    const response = await api.post("/api/chatbot/transcribe", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  };

// Demo Booking
export type DemoBookingRequest = {
  name: string;
  job_title: string;
  email: string;
  phone_number?: string;
  company_name: string;
};

const demoBooking =
  (api: AxiosInstance) =>
  async (req: DemoBookingRequest): Promise<void> => {
    const response = await api.post(
      "/api/subscriptions/enterprise/request",
      req,
    );
    return response.data;
  };

export type CreditsResponse = {
  credits_remaining: number;
  credits_used: number;
  credits_reserved: number;
};

const getCredits =
  (api: AxiosInstance) => async (): Promise<CreditsResponse> => {
    const response = await api.get(`/api/scait/credits`);
    return response.data;
  };

export type CreditPriceResponse = {
  price: number;
  tax_rate: number;
};

const getCreditPricing =
  (api: AxiosInstance) => async (): Promise<CreditPriceResponse> => {
    const response = await api.get(`/api/scait/credits/pricing`);
    return response.data;
  };

export type PurchaseCreditsRequest = {
  number_of_credits: number;
};

const purchaseCredits =
  (api: AxiosInstance) =>
  async (
    req: PurchaseCreditsRequest,
  ): Promise<{ status: string } | undefined> => {
    const response = await api.post(`/api/billing/scait/credits/purchase`, req);
    return response.data;
  };

export type GenerateTokenResponse = {
  status: string;
  token: string;
};

const generateToken =
  (api: AxiosInstance) => async (): Promise<GenerateTokenResponse> => {
    const response = await api.post(`/api/scait/generate-token`);
    return response.data;
  };

export type ContactSupportRequest = {
  name: string;
  job_title: string;
  email: string;
  phone_number?: string;
  company_name: string;
  message: string;
};

const contactSupport =
  (api: AxiosInstance) =>
  async (req: ContactSupportRequest): Promise<void> => {
    const response = await api.post("/api/support/contact", req);
    return response.data;
  };

export type ScaitCreditRequest = {
  language: string;
  reason: string;
  organization_settings_link: string;
};

const requestScaitCredit =
  (api: AxiosInstance) =>
  async (request: ScaitCreditRequest): Promise<void> => {
    await api.post(`/api/scait/credits/request`, request);
  };
